import { useCallback } from 'react';
import { ApolloError } from '@apollo/client';
import { toast } from 'sonner';

import { useSendOpsOrderToPickingMutation } from '@/graphql/mutations/sendOpsOrderToPicking.generated';
import type { OpsOrderFilter } from '@/graphql/generatedTypes';
import i18n from '@/utils/i18n';

interface UseSendToPickingActionOptions {
  /** Optional filter passed alongside ids (list views) */
  filter?: OpsOrderFilter;
  /** Called after a successful mutation. */
  onSuccess?: () => void | Promise<void>;
  /** Called when the mutation partially fails (has_errors = true). */
  onPartialError?: () => void;
}

export function useSendToPickingAction({
  filter,
  onSuccess,
  onPartialError,
}: UseSendToPickingActionOptions = {}) {
  const [sendToPicking, { loading }] = useSendOpsOrderToPickingMutation();

  const handleSendToPicking = useCallback(
    async (ids: string[]) => {
      try {
        const result = await sendToPicking({
          variables: { ...(filter ? { filter } : {}), ids },
        });
        const payload = result.data?.sendOpsOrderToPicking;
        if (payload?.has_errors) {
          toast.error(i18n.t('Order:toastSendToPickingError'));
          onPartialError?.();
        } else {
          await onSuccess?.();
        }
      } catch (err) {
        let reason: string | undefined;
        if (err instanceof ApolloError && err.graphQLErrors.length > 0) {
          const ext = err.graphQLErrors[0].extensions;
          reason =
            (ext?.exception as Record<string, string> | undefined)?.reason ??
            (ext?.reason as string | undefined) ??
            err.graphQLErrors[0].message;
        }
        toast.error(
          i18n.t('Order:toastSendToPickingError'),
          reason ? { description: reason } : undefined,
        );
      }
    },
    [sendToPicking, filter, onSuccess, onPartialError],
  );

  return { handleSendToPicking, pickingLoading: loading };
}
